import { useEffect } from 'react';
import type { UseFormGetFieldState, UseFormGetValues } from 'react-hook-form';

import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';

import {
  type FieldMetadata,
  orderFieldMetadata,
  orderFormV3PanelInventory,
} from '../fieldMetadata';
import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * The dev/test-only `window.__orderFormV3__` surface: the deterministic
 * sensing/actuation API browser-driving agents
 * (and the deferred AI-patch e2e) use instead of scraping MUI widgets through
 * the DOM. Read: getValues / getDirtyFields / getErrors / listFields /
 * getPanelInventory. Act: applyAIPatch / validateField / getAIEditedFields.
 * Production builds never install it.
 */
type OrderFormV3WindowHook = {
  /** The live form values (the schema keys plus the documented runtime extras). */
  getValues: () => OrderFormV3FieldValues;
  /** Schema keys whose live RHF dirty bit is set (nested dirt rolls up to its key). */
  getDirtyFields: () => Partial<Record<keyof NewOrderFormSchemaInterface, true>>;
  /** field → message for every schema key holding a live validation/422 error. */
  getErrors: () => Partial<Record<keyof NewOrderFormSchemaInterface, string>>;
  /** The orderFieldMetadata index (label/description/aiHint/panel/testId per key). */
  listFields: () => Record<keyof NewOrderFormSchemaInterface, FieldMetadata>;
  /** The ordered panel file names per column (the static card set). */
  getPanelInventory: () => typeof orderFormV3PanelInventory;
  validateField: (key: keyof NewOrderFormSchemaInterface) => Promise<boolean>;
  applyAIPatch: (partial: Partial<NewOrderFormSchemaInterface>) => void;
  getAIEditedFields: () => Array<keyof NewOrderFormSchemaInterface>;
};

declare global {
  interface Window {
    __orderFormV3__?: OrderFormV3WindowHook;
  }
}

// The metadata key-set IS the schema key-set (the satisfies gate pins it), so
// it doubles as the iteration order for the per-key live-state reads below.
const orderSchemaKeys = Object.keys(orderFieldMetadata) as Array<
  keyof NewOrderFormSchemaInterface
>;

/**
 * Triggers: Provider mount/unmount, plus identity changes of the handed
 *           callbacks (aiEditedFields changes per AI edit — the reinstall
 *           rebinds getAIEditedFields to the fresh set, projectFormV2
 *           precedent).
 * Writes:   `window.__orderFormV3__` only — install on mount, delete on
 *           unmount. Never form or Provider state.
 * Guards:   `import.meta.env.MODE === 'production'` → never installs (vite
 *           sets MODE to 'development' in dev, 'test' in vitest, 'production'
 *           in builds). Dirty/error reads go through RHF's getFieldState,
 *           which reads the LIVE internal store — the root formState proxy
 *           would serve render-stale snapshots to imperative callers.
 * Legacy:   none — a new agent-facing surface, porting the shipped
 *           ProjectFormV2Context.tsx window hook and extending it to the
 *           full read/act API.
 * Test:     __tests__/OrderFormV3Context.test.tsx (the agent runtime API
 *           suite).
 */
export function useAgentRuntimeApi({
  applyAIPatch,
  aiEditedFields,
  validateField,
  getValues,
  getFieldState,
}: {
  applyAIPatch: (partial: Partial<NewOrderFormSchemaInterface>) => void;
  aiEditedFields: ReadonlySet<keyof NewOrderFormSchemaInterface>;
  validateField: (key: keyof NewOrderFormSchemaInterface) => Promise<boolean>;
  getValues: UseFormGetValues<OrderFormV3FieldValues>;
  getFieldState: UseFormGetFieldState<OrderFormV3FieldValues>;
}): void {
  useEffect(() => {
    if (import.meta.env.MODE === 'production') {
      return undefined;
    }

    window.__orderFormV3__ = {
      getValues: () => getValues(),
      getDirtyFields: () => {
        const dirty: Partial<Record<keyof NewOrderFormSchemaInterface, true>> = {};
        for (const key of orderSchemaKeys) {
          if (getFieldState(key).isDirty) {
            dirty[key] = true;
          }
        }
        return dirty;
      },
      getErrors: () => {
        const errors: Partial<Record<keyof NewOrderFormSchemaInterface, string>> = {};
        for (const key of orderSchemaKeys) {
          const message = getFieldState(key).error?.message;
          if (typeof message === 'string' && message.length > 0) {
            errors[key] = message;
          }
        }
        return errors;
      },
      listFields: () => orderFieldMetadata,
      getPanelInventory: () => orderFormV3PanelInventory,
      validateField,
      applyAIPatch,
      getAIEditedFields: () => [...aiEditedFields],
    };

    return () => {
      delete window.__orderFormV3__;
    };
  }, [applyAIPatch, aiEditedFields, validateField, getValues, getFieldState]);
}
